import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@argink/ui';
import { Search } from 'lucide-react';
import {
  GO_TO_EMPTY_STATE,
  GO_TO_FIELD_LABEL,
  GO_TO_LABEL,
  GO_TO_PLACEHOLDER,
  GO_TO_SHORTCUT_LABEL,
  NAV_PAINEL_LABEL,
  NEW_ENVELOPE_LABEL,
} from '../../lib/copy';

/**
 * "Ir para…" — command palette navegacional (R-UX02-P01/D-101, §"IR PARA…" do
 * contrato). Controle real na sidebar (não decoração): abre por clique OU
 * `Ctrl/Cmd+K`, filtra por rótulo entre as TRÊS únicas superfícies que o mandato
 * §4 (D-100) autoriza encontrar — Painel, Envios, Novo envio — e navega de
 * verdade. **Não é busca de documentos**: não existe campo "buscar" em lugar
 * nenhum desta paleta, de propósito.
 *
 * Dividido em hook (`useGoToPalette`, o estado) + dois componentes de render
 * (`GoToTrigger`, `GoToDialog`) porque o TRIGGER precisa viver DENTRO da
 * navegação da sidebar (para aparecer na gaveta mobile) enquanto o DIALOG
 * precisa viver FORA dela: com a gaveta fechada no mobile, `.argink-app-shell__sidebar`
 * recebe `visibility: hidden` (AppShell.css), e um `<dialog>` aninhado ali dentro
 * herdaria essa visibilidade mesmo promovido ao top layer por `showModal()` — o
 * atalho de teclado global (`Ctrl/Cmd+K`) precisa abrir a paleta com a gaveta
 * FECHADA também. `TenantShell` monta os dois como irmãos, não aninhados.
 */

interface GoToItem {
  to: string;
  label: string;
}

/** Lista própria, e não `PRODUCT_NAV` (lib/copy.ts): a paleta inclui "Novo
 * envio", que não está na sidebar — é CTA da topbar. */
const GO_TO_ITEMS: readonly GoToItem[] = [
  { to: '/dashboard', label: NAV_PAINEL_LABEL },
  /* Literal 'Envios', pelo mesmo motivo documentado em `PRODUCT_NAV`
   * (`lib/copy.ts`): `ENVELOPES_TITLE` só é declarada mais abaixo naquele
   * arquivo, e importar o NOME antes da declaração quebra em TDZ. */
  { to: '/envelopes', label: 'Envios' },
  { to: '/envelopes/new', label: NEW_ENVELOPE_LABEL },
];

export interface GoToPaletteState {
  open: boolean;
  query: string;
  setQuery: (value: string) => void;
  results: readonly GoToItem[];
  openPalette: () => void;
  closePalette: () => void;
  go: (to: string) => void;
  triggerRef: RefObject<HTMLButtonElement>;
  inputRef: RefObject<HTMLInputElement>;
}

export function useGoToPalette(): GoToPaletteState {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  /* Atalho global: funciona com a gaveta mobile aberta OU fechada, porque o
   * listener vive no `document`, não dentro da sidebar. */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (!isShortcut) return;
      event.preventDefault();
      setOpen(true);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  /* Foco entra no campo assim que o diálogo abre; a busca também recomeça em
   * branco a cada abertura — um filtro que sobrevive de uma sessão pra outra da
   * paleta confundiria "por que isto não aparece" com um filtro esquecido. */
  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  /**
   * Escape fecha — EXPLÍCITO, e não só via evento nativo `cancel` do `<dialog>`
   * (que `Dialog.tsx`, `@argink/ui`, já escuta). O `<dialog>` nativo despacha
   * `cancel` quando o MOTOR do navegador reconhece Esc num diálogo modal — um
   * comportamento de renderização, não algo que `user-event` consiga simular
   * disparando `keydown` (achado real: jsdom não fecha o `<dialog>` em resposta
   * a um Esc simulado). O mesmo padrão já existe em `AppShell.tsx` (gaveta) —
   * este listener é redundante com o `cancel` nativo em navegador real
   * (`closePalette` é idempotente), e é o que faz Esc funcionar de verdade sob
   * jsdom/RTL.
   */
  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') closePalette();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function openPalette() {
    setOpen(true);
  }

  /* Fechar SEMPRE devolve o foco ao gatilho — por programa, não por confiança no
   * restore nativo do `<dialog>` (que varia entre motores e não é exercitado por
   * nenhum teste existente deste repo antes desta lane). Cobre Esc, backdrop, o
   * botão × do Dialog e a navegação por um resultado. */
  /* O foco só pode voltar ao gatilho DEPOIS que o `<dialog>` modal saiu do top layer, e o
   * `node.close()` do Dialog só roda no layout effect do render seguinte — um `focus()`
   * síncrono dentro de `closePalette` era no-op silencioso (L4-S, R-UX02-P02). Por isso o
   * restore vive num effect disparado por `open === false`; a re-tentativa por alguns frames é
   * defensiva para motores que liberem o top layer com atraso (o caso do diálogo preso no top
   * layer foi corrigido na origem, em `@argink/ui` Dialog.tsx). */
  const restoreFocus = useRef(false);
  useEffect(() => {
    if (open || !restoreFocus.current) return undefined;
    restoreFocus.current = false;
    let tentativas = 0;
    let frame = 0;
    const tentar = () => {
      const alvo = triggerRef.current;
      if (!alvo) return;
      alvo.focus();
      if (document.activeElement !== alvo && tentativas++ < 5) frame = window.requestAnimationFrame(tentar);
    };
    tentar();
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  function closePalette() {
    restoreFocus.current = true;
    setOpen(false);
  }

  function go(to: string) {
    restoreFocus.current = true;
    setOpen(false);
    navigate(to);
  }

  const term = query.trim().toLocaleLowerCase('pt-BR');
  const results = term
    ? GO_TO_ITEMS.filter((item) => item.label.toLocaleLowerCase('pt-BR').includes(term))
    : GO_TO_ITEMS;

  return { open, query, setQuery, results, openPalette, closePalette, go, triggerRef, inputRef };
}

/** O controle de verdade na sidebar — vive dentro da navegação para aparecer
 * também na gaveta mobile (§"RESPONSIVIDADE": busca continua acessível ali). */
export function GoToTrigger({ palette }: { palette: GoToPaletteState }) {
  return (
    <button
      ref={palette.triggerRef}
      type="button"
      className="go-to-trigger argink-focusable"
      onClick={palette.openPalette}
    >
      <Search aria-hidden="true" size={16} />
      <span className="go-to-trigger__label">{GO_TO_LABEL}</span>
      <kbd className="go-to-trigger__kbd" aria-hidden="true">
        {GO_TO_SHORTCUT_LABEL}
      </kbd>
    </button>
  );
}

/** O diálogo — irmão do `AppShell`, nunca dentro da sidebar (ver o comentário no
 * topo do arquivo). */
export function GoToDialog({ palette }: { palette: GoToPaletteState }) {
  function handleInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' && palette.results.length > 0) {
      event.preventDefault();
      palette.go(palette.results[0]!.to);
    }
  }

  return (
    <Dialog open={palette.open} onClose={palette.closePalette} title={GO_TO_LABEL}>
      <label className="go-to__field">
        <span className="argink-sr-only">{GO_TO_FIELD_LABEL}</span>
        <input
          ref={palette.inputRef}
          type="text"
          className="go-to__input"
          placeholder={GO_TO_PLACEHOLDER}
          value={palette.query}
          onChange={(event) => palette.setQuery(event.target.value)}
          onKeyDown={handleInputKeyDown}
        />
      </label>
      {palette.results.length === 0 ? (
        <p className="go-to__empty">{GO_TO_EMPTY_STATE}</p>
      ) : (
        <ul className="go-to__list">
          {palette.results.map((item) => (
            <li key={item.to}>
              <button
                type="button"
                className="go-to__result argink-focusable"
                onClick={() => palette.go(item.to)}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
